import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthService} from '../../services/auth.service';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';

@Component({
    standalone: false,
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
    public token: string;
    public email: string;
    public restPasswordForm: UntypedFormGroup;
    public error: any;
    public isTokenValidationPending = true;
    public isTokenValid = false;
    public submitted = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private fb: UntypedFormBuilder,
        private authService: AuthService,
        private snackBar: MatSnackBar,
    ) {
        if (this.authService.authenticated) {
            this.router.navigate(['/app']).then(r => r);
        }

        this.token = this.activatedRoute.snapshot.paramMap.get('token')
            ?? this.activatedRoute.snapshot.queryParamMap.get('token');
        this.email = this.activatedRoute.snapshot.queryParamMap.get('email');

        this.activatedRoute.queryParams.subscribe(params => {
            this.token = this.token ?? params.token;
            this.email = params.email ?? this.email;

            if (this.restPasswordForm) {
                this.restPasswordForm.patchValue({
                    token: this.token ?? null,
                    email: this.email ?? null,
                });
            }
        });
    }

    get f() {
        return this.restPasswordForm.controls;
    }

    public ngOnInit(): void {
        this.restPasswordForm = this.fb.group({
            token: [this.token, Validators.required],
            email: [{value: this.email ?? null, disabled: true}, [Validators.required, Validators.email]],
            password: [null, Validators.required],
            password_confirmation: [null, Validators.required],
        });

        this.validateResetToken();
    }

    public onSubmit(): void {
        this.submitted = true;

        if (this.isTokenValidationPending || !this.isTokenValid || this.restPasswordForm.invalid) {
            return;
        }

        const credentials = this.restPasswordForm.getRawValue();

        this.restPasswordForm.disable();

        this.authService.resetPassword(credentials).subscribe(
            () => {
                this.snackBar.open('Password successfully reset.', 'Ok');
                this.router.navigateByUrl('/login').then(r => r);
            },
            (response: HttpErrorResponse) => {
                this.restPasswordForm.enable();
                this.snackBar.open(response?.error?.message, 'Ok');
            }
        );
    }

    private validateResetToken(): void {
        const credentials = {
            token: this.token,
            email: this.email,
        };

        if (!credentials.token || !credentials.email) {
            this.handleInvalidResetLink();
            return;
        }

        this.authService.validateResetPasswordToken(credentials).subscribe({
            next: () => {
                this.isTokenValidationPending = false;
                this.isTokenValid = true;
            },
            error: () => {
                this.handleInvalidResetLink();
            },
        });
    }

    private handleInvalidResetLink(): void {
        this.isTokenValidationPending = false;
        this.isTokenValid = false;
        this.snackBar.open('This password reset link is invalid or has expired. Please request a new one.', 'Ok');
        this.router.navigateByUrl('/login').then(r => r);
    }
}
